import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import Button from "@/components/ui/Button.jsx";
import usePatientPhotos from "@/features/clinical-actions/usePatientPhotos.js";

// Icons (reuse your doctor set)
const ICON_BACK = "/icons/doctor/arrow_back.svg";
const ICON_NEXT = "/icons/doctor/appointments/arrow_next.svg";
const ICON_BEFORE = "/icons/doctor/appointments/arrow_before.svg";
const ICON_CLOSE = "/icons/doctor/close.svg"; // optional (kept for parity)

export default function DoctorPatientPhotos() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const { appointmentId } = useParams();
  const {items:images,access,loading}=usePatientPhotos(appointmentId);

  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());

  const selectedCount = selectedIds.size;
  const hasImages = images.length > 0;

  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerIndex, setViewerIndex] = React.useState(0);

const openViewer = (idx) => {
  setViewerIndex(idx);
  setViewerOpen(true);
};

const closeViewer = () => setViewerOpen(false);

const goPrev = () =>
  setViewerIndex((i) => (i - 1 + images.length) % images.length);

const goNext = () =>
  setViewerIndex((i) => (i + 1) % images.length);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="w-full min-h-screen bg-[rgb(var(--afrimed-shell-background))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
        title={t("clinical.photos.title")}
        onBack={() => {
            if (selectionMode && selectedIds.size > 0) {
            setSelectedIds(new Set());
            setSelectionMode(false);
            } else {
            nav(-1);
            }
        }}
        nakedBack
        backIconSrc={ICON_BACK}
        />

        <div className="mt-4 space-y-4">
        {/* Grid of images (read-only by default) */}
        {(loading || !hasImages) && (
          <TextField56
            elevated tone="surface" readOnly as="textarea" className="min-h-14 flex justify-center flex-col"
            readOnlyValueStyle={{ whiteSpace: "normal", overflow: "visible" }}
            value={t(loading ? "clinical.photos.loading" : access === "required" ? "clinical.photos.required" : access === "unavailable" ? "clinical.photos.unavailable" : "clinical.photos.empty")}
          />
        )}
        {!loading && access !== "granted" && (
          <Button variant="text" onClick={() => nav(`/doctors/appointments/${appointmentId}`)}>{t("clinical.photos.back")}</Button>
        )}

        <div className={`grid grid-cols-2 gap-3 ${hasImages ? "pb-0" : "pb-4"}`}>
          {images.map((img, idx) => {
            const isSelected = selectedIds.has(img.id);

            return (
              <button
                key={img.id}
                type="button"
                onClick={() => {
                if (selectionMode) toggleSelect(img.id);
                else openViewer(idx);
                }}
                className="
                  relative
                  rounded-[28px]
                  overflow-hidden
                  bg-neutral-200
                  aspect-[1/1]
                "
                style={{ WebkitTapHighlightColor: "transparent" }}
              >
                <img
                  src={img.url}
                  alt=""
                  className="h-full w-full object-cover"
                  draggable="false"
                />

                {selectionMode && (
                  <div className="absolute top-2 right-2">
                    <div
                      className={`
                        h-6 w-6 rounded-full border border-white
                        flex items-center justify-center
                        ${isSelected ? "bg-primary" : "bg-[rgba(0,0,0,0.24)]"}
                      `}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-1 pt-1 text-body-medium text-on-surface-var">
          {t("clinical.photos.readonly")}
        </div>
        </div>
      </div>
    
    {viewerOpen && images[viewerIndex] ? (
    <div className="fixed inset-0 z-50">
        {/* scrim */}
        <button
        type="button"
        className="absolute inset-0 bg-black/70"
        onClick={closeViewer}
        aria-label="Close image viewer"
        />

        {/* Fullscreen viewer layer */}
        <div className="relative h-full w-full">
        {/* ✅ RAIL-ALIGNED TOP CONTROLS (matches your 720/1008 rails) */}
        <div className="absolute left-0 right-0 top-0 z-10">
            <div className="mx-auto w-full max-w-[720px] lg:max-w-[1008px] px-4 pt-5">
            <div className="flex items-center justify-between">
                {/* Close */}
                <button
                type="button"
                onClick={closeViewer}
                aria-label="Close"
                className="
                    h-11 w-11 rounded-[14px]
                    inline-flex items-center justify-center
                    bg-white/10 hover:bg-white/15
                "
                style={{ WebkitTapHighlightColor: "transparent" }}
                >
                {/* If the SVG has wonky viewBox padding, this tiny nudge fixes centering */}
                <img
                    src={ICON_CLOSE}
                    alt=""
                    className="h-5 w-5 translate-y-[2px] translate-x-[2px]"
                    draggable="false"
                    style={{ filter: "brightness(0) invert(1)" }}
                />
                </button>

                {/* Counter (use your token class) */}
                <div className="text-body-large text-white">
                {viewerIndex + 1}/{images.length}
                </div>
            </div>
            </div>
        </div>

        {/* Centered image frame (large on desktop) */}
        <div className="h-full w-full flex items-center justify-center px-4 pb-8 pt-20">
            <div className="relative w-full h-full max-w-[1400px] max-h-[92vh]">
            {/* Image */}
            <div className="w-full h-full bg-black rounded-[28px] overflow-hidden">
                <img
                src={images[viewerIndex].url}
                alt=""
                className="w-full h-full object-contain"
                draggable="false"
                />
            </div>

            {/* ✅ NAV ARROWS: rail-aligned + SVG icons + white */}
            {images.length > 1 ? (
                <>
                <button
                    type="button"
                    onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                    }}
                    aria-label="Previous image"
                    className="
                    absolute left-[-35px] top-1/2 -translate-y-1/2
                    h-11 w-11 rounded-full
                    inline-flex items-center justify-center
                    "
                    style={{ WebkitTapHighlightColor: "transparent" }}
                >
                    <img
                    src={ICON_BEFORE}
                    alt=""
                    className="h-7 w-7"
                    draggable="false"
                    style={{ filter: "brightness(0) invert(1)" }}
                    />
                </button>

                <button
                    type="button"
                    onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                    }}
                    aria-label="Next image"
                    className="
                    absolute right-[-35px] top-1/2 -translate-y-1/2
                    h-11 w-11 rounded-full
                    inline-flex items-center justify-center
                    "
                    style={{ WebkitTapHighlightColor: "transparent" }}
                >
                    <img
                    src={ICON_NEXT}
                    alt=""
                    className="h-7 w-7"
                    draggable="false"
                    style={{ filter: "brightness(0) invert(1)" }}
                    />
                </button>
                </>
            ) : null}
            </div>
        </div>
        </div>
    </div>
    ) : null}
    </div>
  );
}
